import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { useAppDispatch, useAppSelector } from '@hooks/statehooks';

import ImageGalleryNavigationBar, {
  SubButtons,
  MainButtons,
} from '../navigationbar';
import Gallery from '../gallery';
import View360 from '../view360';
import { getVehicleGalleryImages } from '@store/reducers/vehicleimagegallery';

import '@styles/configure/imagegallery.scss';

import { Vehicle } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicle';

type DivProps = React.HTMLAttributes<HTMLDivElement>;

export interface ImageGalleryContainerProps extends DivProps {
  open?: boolean;
  onClose?: Function;
  vehicle: Vehicle;
}

const ImageGalleryContainer: React.FC<ImageGalleryContainerProps> = ({
  children,
  onClose,
  vehicle,
}) => {
  const { modelCode, doors, train, year, trim } = vehicle;
  const [mainView, setMainView] = useState<string>(MainButtons.ImageGallery);
  const [interiorExterior, setInteriorExterior] = useState<string>(
    SubButtons.Exterior
  );
  const dispatch = useAppDispatch();
  const vehicleImages = useAppSelector((state) => state.vehicleImageGallery);

  const hasFetchedData = useRef(false);
  useEffect(() => {
    if (!hasFetchedData.current) {
      if (modelCode && vehicleImages.imageList.length === 0) {
        dispatch(
          getVehicleGalleryImages({ modelCode, doors, trim, train, year })
        );
      }
    }
  }, [modelCode, trim, doors, train]);

  return (
    <div
      className="image-gallery-container"
      data-testid="image-gallery-container"
      data-cy="image-gallery-container"
    >
      <ImageGalleryNavigationBar
        onClose={onClose}
        onMainSelected={(mainSelected) => setMainView(mainSelected)}
        onSubSelected={(subSelected) => setInteriorExterior(subSelected)}
        mainSelected={mainView}
        subSelected={interiorExterior}
      />
      <div
        data-testid="image-gallery-container-content"
        data-cy="image-gallery-container-content"
        className="image-gallery-container__content"
      >
        <>
          <>
            <Gallery
              onMainSelected={(mainSelected) => setMainView(mainSelected)}
              className={cn(
                'image-gallery-container__content-gallery',
                mainView === MainButtons.ImageGallery
                  ? 'image-gallery-container__content-gallery--active'
                  : 'image-gallery-container__content-gallery--passive'
              )}
              vehicle={vehicle}
            />
          </>
          <>
            <View360
              activeView={interiorExterior}
              modelCode={modelCode}
              doors={doors}
              train={train}
              year={year}
              trim={trim}
              className={cn(
                'image-gallery-container__content-view360',
                mainView === MainButtons.View360
                  ? 'image-gallery-container__content-view360--active'
                  : 'image-gallery-container__content-view360--passive'
              )}
            />
          </>
          {children}
        </>
      </div>
    </div>
  );
};

export default ImageGalleryContainer;
